import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Search, User, Building2, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
interface ServiceType {
  id: string;
  name: string;
  description: string | null;
  category: string;
  estimatedDays: string | null;
  isActive: boolean;
  credentialTypeIds?: number[];
}

const formSchema = z.object({
  contactId: z.number().min(1, "Contact is required"),
  companyId: z.number().min(1, "Company is required"),
  carrierId: z.number().nullable().optional(),
  secondaryContactId: z.number().optional(),
  serviceType: z.string().min(1, "Service type is required"),
  status: z.enum(["new", "collecting_inputs", "validating", "awaiting_payment", "preparing_documents", "awaiting_signatures", "submitted", "awaiting_client_filing", "work_complete", "closed", "cancelled", "pending", "in_progress", "awaiting_documents", "completed"], {
    error: (issue) => issue.input === undefined ? "Status is required" : undefined,
  }),
  priority: z.enum(["low", "medium", "high"]).optional(),
  estimatedCompletionDate: z.string().optional(),
  description: z.string().optional(),
  assignedToUserId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface EditWorkOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  workOrder: any;
  users: any[];
  paymentStatus?: { stripeInvoiceId?: string | null } | null;
}

export default function EditWorkOrderModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  workOrder,
  users,
  paymentStatus,
}: EditWorkOrderModalProps) {
  const { user: currentUser } = useAuth();
  const canEditStatus = currentUser?.role === "admin" || currentUser?.role === "staff";

  // ── Lock state ────────────────────────────────────────────────────────────
  const hasCompletedTasks = workOrder?.tasks?.some((t: any) => t.completed) ?? false;
  const hasStripeInvoice = !!paymentStatus?.stripeInvoiceId;
  const isLocked = hasCompletedTasks || hasStripeInvoice;

  const lockReason = hasCompletedTasks
    ? "One or more tasks have been completed. To change the service, contact, or company, mark all tasks incomplete first or create a new work order."
    : hasStripeInvoice
    ? "An invoice has been created in Stripe. To change the service, contact, or company, void the invoice first or create a new work order."
    : null;

  // ── Unlocked contact/company local state (mirrors create modal) ───────────
  const [contactSearch, setContactSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [availableCarriers, setAvailableCarriers] = useState<any[]>([]);
  const [availableSecondaryContacts, setAvailableSecondaryContacts] = useState<any[]>([]);
  const [contactError, setContactError] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [requestingContactServiceBureauId, setRequestingContactServiceBureauId] = useState<number | null>(null);
  const [selectedContactServiceBureauName, setSelectedContactServiceBureauName] = useState<string | null>(null);
  const [showContactChangeDropdown, setShowContactChangeDropdown] = useState(false);
  // Prevents pre-populate effect from re-firing after user explicitly clears the contact
  const contactPrePopulated = useRef(false);

  // ── Form ─────────────────────────────────────────────────────────────────
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactId: 0,
      companyId: 0,
      carrierId: undefined,
      secondaryContactId: undefined,
      serviceType: "",
      status: "new",
      description: "",
      priority: "medium",
      estimatedCompletionDate: "",
      assignedToUserId: "unassigned",
    },
  });

  const { handleSubmit, setValue, watch, control, reset, formState: { errors } } = form;

  const selectedContactId = watch("contactId");
  const selectedCompanyId = watch("companyId");
  const selectedCarrierId = watch("carrierId");
  const selectedSecondaryContactId = watch("secondaryContactId");
  const watchedServiceType = watch("serviceType");

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ["/api/service-types"],
  });
  const activeServiceTypes = serviceTypes.filter((st) => st.isActive);

  // All contacts for search (enabled when typing or pre-populating)
  const { data: contacts = [] } = useQuery<any[]>({
    queryKey: ["/api/contacts"],
    enabled: !isLocked && (contactSearch.length >= 1 || !!workOrder?.contactId),
  });

  // Contact's linked companies (drives available company list)
  const { data: contactCompanies = [], isLoading: isLoadingContactCompanies } = useQuery<any[]>({
    queryKey: [`/api/contacts/${selectedContactId}/companies`],
    enabled: selectedContactId > 0 && !isLocked,
  });

  // All companies (needed for service bureau contacts who can pick any company)
  const { data: allCompanies = [] } = useQuery<any[]>({
    queryKey: ["/api/companies"],
    enabled: !isLocked,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  // Carriers for selected company
  const { data: companyCarriers = [] } = useQuery<any[]>({
    queryKey: [`/api/companies/${selectedCompanyId}/carriers`],
    enabled: selectedCompanyId > 0,
  });

  // Secondary contacts: company contacts + service bureau contacts (same endpoint as create modal)
  const { data: secondaryContactOptions = [] } = useQuery<any[]>({
    queryKey: [`/api/secondary-contacts/${selectedCompanyId}`, requestingContactServiceBureauId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (requestingContactServiceBureauId) {
        params.set("serviceBureauId", requestingContactServiceBureauId.toString());
      }
      const url = `/api/secondary-contacts/${selectedCompanyId}${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: selectedCompanyId > 0,
  });

  // Other contacts from same service bureau (for contact-switch dropdown)
  const { data: sameCompanyContacts = [] } = useQuery<any[]>({
    queryKey: [`/api/companies/${requestingContactServiceBureauId}/contacts`],
    enabled: requestingContactServiceBureauId !== null && requestingContactServiceBureauId > 0 && !isLocked,
  });

  // Resolved service rate for billing display
  const { data: resolvedRate } = useQuery<{ rate: string; isCustom: boolean; rateId: number } | null>({
    queryKey: ["/api/resolved-service-rate", selectedCompanyId, watchedServiceType],
    enabled: selectedCompanyId > 0 && !!watchedServiceType,
    queryFn: async () => {
      const response = await fetch(`/api/resolved-service-rate/${selectedCompanyId}/${watchedServiceType}`);
      if (!response.ok) return null;
      return response.json();
    },
  });

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filteredContacts = contacts.filter(
    (c: any) =>
      c.firstName?.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.lastName?.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.phone?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const filteredCompanies = useMemo(() => {
    if (!companySearch) return availableCompanies;
    const lower = companySearch.toLowerCase();
    return availableCompanies.filter(
      (c: any) =>
        c.name?.toLowerCase().includes(lower) ||
        c.dotProfile?.dotNumber?.toLowerCase().includes(lower)
    );
  }, [availableCompanies, companySearch]);

  const selectedCompanyDisplay = useMemo(() => {
    const company = availableCompanies.find((c: any) => c.id === selectedCompanyId);
    if (!company) return "";
    return company.dotProfile?.dotNumber
      ? `${company.name} - ${company.dotProfile.dotNumber}`
      : company.name;
  }, [availableCompanies, selectedCompanyId]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleContactSelect = (contact: any) => {
    setSelectedContact(contact);
    setValue("contactId", contact.id);
    setContactSearch(`${contact.firstName} ${contact.lastName}`);
    setContactError("");
    setShowContactChangeDropdown(false);
    setValue("companyId", 0);
    setValue("carrierId", undefined);
    setValue("secondaryContactId", undefined);
    setCompanySearch("");
    setAvailableCompanies([]);
    setAvailableCarriers([]);
    setAvailableSecondaryContacts([]);
    setRequestingContactServiceBureauId(null);
    setSelectedContactServiceBureauName(null);
  };

  const handleContactSwitch = (contact: any) => {
    setSelectedContact(contact);
    setValue("contactId", contact.id);
    setContactSearch(`${contact.firstName} ${contact.lastName}`);
    setShowContactChangeDropdown(false);
  };

  const handleCompanySelect = (company: any) => {
    setValue("companyId", company.id);
    setCompanySearch(
      company.dotProfile?.dotNumber
        ? `${company.name} - ${company.dotProfile.dotNumber}`
        : company.name
    );
    setShowCompanyDropdown(false);
    setValue("carrierId", undefined);
    setValue("secondaryContactId", undefined);
  };

  const formatCarrierDisplay = (carrier: any) => {
    const prefix = carrier.docketPrefix || "";
    const docket = carrier.docketNumber || "";
    if (prefix && docket) return `${carrier.name} - ${prefix}-${docket}`;
    return carrier.name;
  };

  // ── Effects ───────────────────────────────────────────────────────────────

  // Reset form values and all contact/company local state every time the modal opens
  useEffect(() => {
    if (!open || !workOrder) return;

    reset({
      contactId: workOrder.contactId || 0,
      companyId: workOrder.companyId || 0,
      carrierId: workOrder.carrierId || undefined,
      secondaryContactId: workOrder.secondaryContactId || undefined,
      serviceType: workOrder.serviceType || "",
      status: workOrder.status || "pending",
      description: workOrder.description || "",
      priority: "medium",
      estimatedCompletionDate: workOrder.estimatedCompletionDate
        ? new Date(workOrder.estimatedCompletionDate).toISOString().split("T")[0]
        : "",
      assignedToUserId: workOrder.assignedToUserId || "unassigned",
    });

    // Reset unlocked contact/company state so pre-population runs fresh
    contactPrePopulated.current = false;
    companyPrePopulated.current = false;
    setContactSearch("");
    setCompanySearch("");
    setSelectedContact(null);
    setAvailableCompanies([]);
    setAvailableCarriers([]);
    setAvailableSecondaryContacts([]);
    setContactError("");
    setRequestingContactServiceBureauId(null);
    setSelectedContactServiceBureauName(null);
    setShowCompanyDropdown(false);
    setShowContactChangeDropdown(false);
  }, [open]);

  // Pre-populate selected contact on open (from contacts list) — only once per modal open
  useEffect(() => {
    if (!isLocked && workOrder?.contactId && contacts.length > 0 && !contactPrePopulated.current) {
      const contact = contacts.find((c: any) => c.id === workOrder.contactId);
      if (contact) {
        contactPrePopulated.current = true;
        setSelectedContact(contact);
        setContactSearch(`${contact.firstName} ${contact.lastName}`);
      }
    }
  }, [workOrder, contacts, isLocked]);

  // Drive availableCompanies from contactCompanies (service bureau → all; regular → their companies)
  useEffect(() => {
    if (isLoadingContactCompanies || selectedContactId === 0 || isLocked) return;

    setContactError("");

    if (contactCompanies.length === 0) {
      setContactError("This contact has no company relationships.");
      setAvailableCompanies([]);
      return;
    }

    const serviceBureauRel = contactCompanies.find(
      (cc: any) => cc.company?.type === "service_bureau"
    );

    if (serviceBureauRel) {
      setRequestingContactServiceBureauId(serviceBureauRel.company.id);
      setSelectedContactServiceBureauName(serviceBureauRel.company.name);
      const sorted = [...allCompanies].sort((a: any, b: any) => a.name.localeCompare(b.name));
      setAvailableCompanies(sorted);
    } else {
      setRequestingContactServiceBureauId(null);
      setSelectedContactServiceBureauName(null);
      const sorted = contactCompanies
        .map((cc: any) => ({ ...cc.company, isPrimary: cc.isPrimary }))
        .sort((a: any, b: any) => {
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return a.name.localeCompare(b.name);
        });
      setAvailableCompanies(sorted);
      if (sorted.length === 1) {
        setValue("companyId", sorted[0].id);
        setCompanySearch(
          sorted[0].dotProfile?.dotNumber
            ? `${sorted[0].name} - ${sorted[0].dotProfile.dotNumber}`
            : sorted[0].name
        );
      }
    }
  }, [contactCompanies, selectedContactId, isLoadingContactCompanies, allCompanies, isLocked]);

  // Pre-populate company from workOrder after availableCompanies loads
  const companyPrePopulated = useRef(false);
  useEffect(() => {
    if (
      !isLocked &&
      workOrder?.companyId &&
      availableCompanies.length > 0 &&
      selectedCompanyId === 0 &&
      !companyPrePopulated.current
    ) {
      const company = availableCompanies.find((c: any) => c.id === workOrder.companyId);
      if (company) {
        companyPrePopulated.current = true;
        setValue("companyId", company.id);
        setCompanySearch(
          company.dotProfile?.dotNumber
            ? `${company.name} - ${company.dotProfile.dotNumber}`
            : company.name
        );
      }
    }
  }, [availableCompanies, workOrder, isLocked, selectedCompanyId]);

  // Sync carriers to local state + auto-select if only one
  useEffect(() => {
    setAvailableCarriers(companyCarriers);
  }, [companyCarriers]);

  useEffect(() => {
    if (availableCarriers.length === 1 && !selectedCarrierId) {
      setValue("carrierId", availableCarriers[0].id);
    }
  }, [availableCarriers, selectedCarrierId]);

  // Sync secondary contacts, excluding the primary contact
  useEffect(() => {
    if (secondaryContactOptions.length > 0) {
      setAvailableSecondaryContacts(
        secondaryContactOptions.filter((c: any) => c.id !== selectedContactId)
      );
    } else {
      setAvailableSecondaryContacts([]);
      setValue("secondaryContactId", undefined);
    }
  }, [secondaryContactOptions, selectedContactId]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleFormSubmit = (data: FormData) => {
    const baseData = {
      ...data,
      estimatedCompletionDate: data.estimatedCompletionDate
        ? new Date(data.estimatedCompletionDate)
        : null,
      assignedToUserId: data.assignedToUserId === "unassigned" ? null : data.assignedToUserId,
      secondaryContactId: data.secondaryContactId || null,
      carrierId: data.carrierId ?? null,
    };
    // Non-admin/staff users cannot change status — strip it from the payload
    // to avoid triggering the server-side 403 authz check
    if (canEditStatus) {
      onSubmit(baseData as unknown as FormData);
    } else {
      const { status: _omittedStatus, ...dataWithoutStatus } = baseData;
      onSubmit(dataWithoutStatus as unknown as FormData);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Work Order</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">

            {/* ── Contact & Company ──────────────────────────────────────── */}
            {isLocked ? (
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm space-y-1">
                  <p>
                    <strong>Contact:</strong>{" "}
                    {workOrder?.requestingContact
                      ? `${workOrder.requestingContact.firstName} ${workOrder.requestingContact.lastName}`
                      : "Not specified"}
                  </p>
                  <p>
                    <strong>Company:</strong> {workOrder?.company?.name || "Not specified"}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Contact search */}
                <div className="space-y-2">
                  <Label>Requesting Contact *</Label>

                  {selectedContact ? (
                    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">
                              {selectedContact.firstName} {selectedContact.lastName}
                            </div>
                            {selectedContact.email && (
                              <div className="text-sm text-gray-500">{selectedContact.email}</div>
                            )}
                            {selectedContactServiceBureauName && (
                              <div className="flex items-center gap-1 mt-1">
                                <Building2 className="h-3 w-3 text-blue-600" />
                                <span className="text-xs text-blue-600 font-medium">
                                  {selectedContactServiceBureauName}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Change dropdown — only for service bureau contacts with multiple people */}
                        {requestingContactServiceBureauId && sameCompanyContacts.length > 1 && (
                          <div className="relative">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowContactChangeDropdown(!showContactChangeDropdown)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Change <ChevronDown className="h-3 w-3 ml-1" />
                            </Button>
                            {showContactChangeDropdown && (
                              <div className="absolute right-0 top-full mt-1 w-64 max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg z-50">
                                {sameCompanyContacts
                                  .filter((cc: any) => cc.contact?.id !== selectedContact.id)
                                  .map((cc: any) => (
                                    <button
                                      key={cc.contact?.id}
                                      type="button"
                                      onClick={() => handleContactSwitch(cc.contact)}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="font-medium text-sm">
                                        {cc.contact?.firstName} {cc.contact?.lastName}
                                      </div>
                                      {cc.contact?.email && (
                                        <div className="text-xs text-gray-500">{cc.contact.email}</div>
                                      )}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedContact(null);
                          setValue("contactId", 0);
                          setContactSearch("");
                          setAvailableCompanies([]);
                          setValue("companyId", 0);
                          setValue("carrierId", undefined);
                          setValue("secondaryContactId", undefined);
                          setRequestingContactServiceBureauId(null);
                          setSelectedContactServiceBureauName(null);
                          companyPrePopulated.current = false;
                        }}
                        className="text-xs text-gray-400 hover:text-gray-600 mt-2"
                      >
                        Search for a different contact
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search by name, email, or phone..."
                          value={contactSearch}
                          onChange={(e) => {
                            setContactSearch(e.target.value);
                            if (selectedContact) {
                              setSelectedContact(null);
                              setValue("contactId", 0);
                            }
                          }}
                          className="pl-8"
                        />
                      </div>
                      {contactSearch.length >= 1 && (
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white">
                          {filteredContacts.length > 0 ? (
                            filteredContacts.map((contact: any) => (
                              <button
                                key={contact.id}
                                type="button"
                                onClick={() => handleContactSelect(contact)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium">
                                  {contact.firstName} {contact.lastName}
                                </div>
                                {contact.email && (
                                  <div className="text-sm text-gray-500">{contact.email}</div>
                                )}
                                {contact.phone && (
                                  <div className="text-sm text-gray-500">{contact.phone}</div>
                                )}
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">No contacts found</div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {contactError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{contactError}</AlertDescription>
                    </Alert>
                  )}
                  {errors.contactId && (
                    <p className="text-sm text-red-600">{errors.contactId.message}</p>
                  )}
                </div>

                {/* Company search — shown after contact selected */}
                {availableCompanies.length > 0 && (
                  <div className="space-y-2">
                    <Label>Company *</Label>
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search companies..."
                        value={selectedCompanyId > 0 ? selectedCompanyDisplay : companySearch}
                        onChange={(e) => {
                          setCompanySearch(e.target.value);
                          setShowCompanyDropdown(true);
                          if (selectedCompanyId > 0) {
                            setValue("companyId", 0);
                            setValue("carrierId", undefined);
                            setValue("secondaryContactId", undefined);
                          }
                        }}
                        onFocus={() => setShowCompanyDropdown(true)}
                        className="pl-8"
                      />
                    </div>
                    {showCompanyDropdown && selectedCompanyId === 0 && (
                      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white">
                        {filteredCompanies.length > 0 ? (
                          filteredCompanies.slice(0, 50).map((company: any) => {
                            const isBlocked = company.status === 'blocked';
                            return (
                            <button
                              key={company.id}
                              type="button"
                              onClick={() => !isBlocked && handleCompanySelect(company)}
                              disabled={isBlocked}
                              className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 ${isBlocked ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-100'}`}
                            >
                              <div className="font-medium flex items-center gap-2">
                                {company.name}
                                {company.dotProfile?.dotNumber && (
                                  <span className="text-muted-foreground">
                                    {" "}- {company.dotProfile.dotNumber}
                                  </span>
                                )}
                                {isBlocked && (
                                  <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Blocked</span>
                                )}
                              </div>
                              {company.isPrimary && !isBlocked && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                                  Primary
                                </span>
                              )}
                            </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">No companies found</div>
                        )}
                        {filteredCompanies.length > 50 && (
                          <div className="px-3 py-2 text-sm text-gray-400 text-center">
                            Type to filter more companies...
                          </div>
                        )}
                      </div>
                    )}
                    {errors.companyId && (
                      <p className="text-sm text-red-600">{errors.companyId.message}</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Carrier ────────────────────────────────────────────────── */}
            {selectedCompanyId > 0 && (
              <div className="space-y-2">
                <Label>Carrier (Optional)</Label>
                <Select
                  value={selectedCarrierId ? selectedCarrierId.toString() : ""}
                  onValueChange={(value) =>
                    setValue("carrierId", value ? parseInt(value) : undefined)
                  }
                >
                  <SelectTrigger data-testid="select-carrier">
                    <SelectValue
                      placeholder={
                        availableCarriers.length === 0
                          ? "No carriers available"
                          : "Select a carrier..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {availableCarriers.length > 0 ? (
                      availableCarriers.map((carrier: any) => (
                        <SelectItem
                          key={carrier.id}
                          value={carrier.id.toString()}
                          data-testid={`select-carrier-option-${carrier.id}`}
                        >
                          {formatCarrierDisplay(carrier)}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-carriers" disabled>
                        No carriers for this company
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ── Service & Status ────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name="serviceType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLocked}
                    >
                      <FormControl>
                        <SelectTrigger className={isLocked ? "opacity-60 cursor-not-allowed" : ""}>
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activeServiceTypes.map((st) => (
                          <SelectItem key={st.id} value={st.id}>
                            {st.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isLocked && resolvedRate && (
                      <div
                        className={`text-sm mt-1 px-2 py-1 rounded ${
                          resolvedRate.isCustom
                            ? "bg-primary/10 text-primary"
                            : "bg-gray-100 text-gray-700"
                        }`}
                        data-testid="text-applicable-rate"
                      >
                        Rate: ${resolvedRate.rate}
                        {resolvedRate.isCustom ? " (Custom)" : " (Standard)"}
                      </div>
                    )}
                    {isLocked && lockReason && (
                      <Alert variant="default" className="mt-2 py-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">{lockReason}</AlertDescription>
                      </Alert>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {canEditStatus && (
                <FormField
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                          <SelectItem value="validating">Validating</SelectItem>
                          <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                          <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                          <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                          <SelectItem value="work_complete">Work Complete</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* ── Priority & Assigned To ──────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="assignedToUserId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assigned To</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users &&
                          users.map((user: any) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* ── Secondary Contact ──────────────────────────────────────── */}
            {selectedCompanyId > 0 && (
              <div className="space-y-2">
                <Label>Secondary Contact (Optional)</Label>
                <Select
                  value={selectedSecondaryContactId ? selectedSecondaryContactId.toString() : ""}
                  onValueChange={(value) =>
                    setValue("secondaryContactId", value ? parseInt(value) : undefined)
                  }
                >
                  <SelectTrigger data-testid="select-secondary-contact">
                    <SelectValue
                      placeholder={
                        availableSecondaryContacts.length === 0
                          ? "No secondary contacts available"
                          : "Select a secondary contact..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {availableSecondaryContacts.length > 0 ? (
                      availableSecondaryContacts.map((contact: any) => (
                        <SelectItem key={contact.id} value={contact.id.toString()}>
                          {contact.firstName} {contact.lastName}
                          {contact.companyName && (
                            <span className="ml-2 text-xs text-gray-500">
                              ({contact.companyName})
                            </span>
                          )}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-secondary-contacts" disabled>
                        No secondary contacts available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ── Date & Description ─────────────────────────────────────── */}
            <FormField
              control={control}
              name="estimatedCompletionDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estimated Completion Date</FormLabel>
                  <FormControl>
                    <Input type="date" placeholder="Select date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter work order details..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Work Order"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
